import { createElement, useCallback, useMemo, type ReactNode } from "react";

import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import { default as classes } from "./_.module.scss";
import { type ChatMarkdownProps } from "./_types";

/**
 * ChatMarkdown Component Hooks
 */
export const useChatMarkdown = ({ content }: ChatMarkdownProps) => {
  /**
   * Wrap table elements in a scrollable container
   */
  const TableWrapper = useCallback(
    ({ children }: { children?: ReactNode }) =>
      createElement(
        "div",
        { className: classes.tableWrapper },
        createElement("table", null, children),
      ),
    [],
  );

  /**
   * Render unordered list elements
   */
  const UlWrapper = useCallback(
    ({ children }: { children?: ReactNode }) =>
      createElement("ul", null, children),
    [],
  );

  /** Render ordered list elements */
  const OlWrapper = useCallback(
    ({ children }: { children?: ReactNode }) =>
      createElement("ol", null, children),
    [],
  );

  /** Render code elements with syntax class */
  const CodeWrapper = useCallback(
    (props: { children?: ReactNode; className?: string }) =>
      createElement("code", { className: props.className }, props.children),
    [],
  );

  /**
   * Memoised react-markdown components map
   */
  const components: import("react-markdown").Components = useMemo(
    () => ({
      table: TableWrapper,
      ul: UlWrapper,
      ol: OlWrapper,
      code: CodeWrapper,
    }),
    [TableWrapper, UlWrapper, OlWrapper, CodeWrapper],
  );

  /** Remark plugins (GFM for tables, strikethrough, tasklists) */
  const remarkPlugins = useMemo(() => [remarkGfm], []);

  /** Rehype plugins (syntax highlighting for code blocks) */
  const rehypePlugins = useMemo(() => [rehypeHighlight], []);

  return useMemo(
    () => ({
      states: {
        content,
        components,
        remarkPlugins,
        rehypePlugins,
      },
    }),
    [content, components, remarkPlugins, rehypePlugins],
  );
};
